import { useState } from "react";
import { Pencil, Trash2, DoorOpen } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { Button } from "@/shared/components/Button";
import { FormularioAmbiente } from "@/modules/reservas/components/FormularioAmbiente";
import {
  useAmbientes,
  useCriarAmbiente,
  useAtualizarAmbiente,
  useExcluirAmbiente,
} from "@/modules/reservas/hooks/useAmbientes";
import type { Ambiente, DadosAmbiente } from "@/modules/reservas/services/ambientesService";

export function GerenciarAmbientes() {
  const { data: ambientes, isLoading } = useAmbientes();
  const criar = useCriarAmbiente();
  const atualizar = useAtualizarAmbiente();
  const excluir = useExcluirAmbiente();

  const [modalAberto, setModalAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<Ambiente | null>(null);
  const [erroForm, setErroForm] = useState<string | null>(null);
  const [paraExcluir, setParaExcluir] = useState<Ambiente | null>(null);
  const [erroExcluir, setErroExcluir] = useState<string | null>(null);
  const [paraDesativar, setParaDesativar] = useState<Ambiente | null>(null);
  const [erroToggle, setErroToggle] = useState<string | null>(null);

  function abrirCriacao() {
    setEmEdicao(null);
    setErroForm(null);
    setModalAberto(true);
  }
  function abrirEdicao(a: Ambiente) {
    setEmEdicao(a);
    setErroForm(null);
    setModalAberto(true);
  }

  async function handleSubmit(dados: DadosAmbiente) {
    setErroForm(null);
    try {
      if (emEdicao) await atualizar.mutateAsync({ id: emEdicao.id, dados });
      else await criar.mutateAsync(dados);
      setModalAberto(false);
    } catch (e) {
      setErroForm(e instanceof Error ? e.message : "Não foi possível salvar o ambiente.");
    }
  }

  async function confirmarExclusao() {
    if (!paraExcluir) return;
    setErroExcluir(null);
    try {
      await excluir.mutateAsync(paraExcluir.id);
      setParaExcluir(null);
    } catch (e) {
      setParaExcluir(null);
      setErroExcluir(e instanceof Error ? e.message : "Não foi possível excluir o ambiente.");
    }
  }

  async function handleToggleAtivo(a: Ambiente) {
    if (a.ativo) {
      setParaDesativar(a);
    } else {
      setErroToggle(null);
      try {
        await atualizar.mutateAsync({ id: a.id, dados: { ...a, ativo: true } });
      } catch (e) {
        setErroToggle(e instanceof Error ? e.message : "Não foi possível ativar o ambiente.");
      }
    }
  }

  async function confirmarDesativacao() {
    if (!paraDesativar) return;
    const a = paraDesativar;
    setParaDesativar(null);
    setErroToggle(null);
    try {
      await atualizar.mutateAsync({ id: a.id, dados: { ...a, ativo: false } });
    } catch (e) {
      setErroToggle(e instanceof Error ? e.message : "Não foi possível desativar o ambiente.");
    }
  }

  return (
    <>
      <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-text">Ambientes</h1>
          <Button onClick={abrirCriacao}>+ Novo ambiente</Button>
        </div>
        <p className="mt-1 text-sm text-muted">
          Salas e espaços que podem ser reservados ou usados por eventos.
        </p>

        {erroExcluir && (
          <p role="alert" className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
            {erroExcluir}
          </p>
        )}

        {erroToggle && (
          <p role="alert" className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
            {erroToggle}
          </p>
        )}

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
          {isLoading ? (
            <p className="p-6 text-sm text-muted">Carregando...</p>
          ) : !ambientes?.length ? (
            <p className="p-6 text-sm text-muted">Nenhum ambiente cadastrado.</p>
          ) : (
            <ul>
              {ambientes.map((a, idx) => (
                <li
                  key={a.id}
                  className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                    <DoorOpen size={20} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-text">{a.nome}</p>
                    <p className="mt-0.5 text-xs text-muted">
                      {a.capacidade != null && <span>{a.capacidade} lugares · </span>}
                      <span
                        className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                          a.ativo ? "bg-success/10 text-success" : "bg-surface-2 text-muted"
                        }`}
                      >
                        {a.ativo ? "Ativo" : "Inativo"}
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={a.ativo}
                      aria-label={a.ativo ? `Desativar ${a.nome}` : `Ativar ${a.nome}`}
                      disabled={atualizar.isPending}
                      onClick={() => handleToggleAtivo(a)}
                      className="flex h-11 w-11 shrink-0 items-center justify-center disabled:opacity-60"
                    >
                      <span
                        aria-hidden="true"
                        className={`relative h-6 w-11 rounded-full transition-colors ${
                          a.ativo ? "bg-primary" : "bg-border"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-transform ${
                            a.ativo ? "left-0.5 translate-x-5" : "left-0.5"
                          }`}
                        />
                      </span>
                    </button>
                    <button
                      onClick={() => abrirEdicao(a)}
                      aria-label="Editar"
                      className="flex h-11 w-11 items-center justify-center rounded-lg text-info transition-colors hover:bg-info/10"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setParaExcluir(a)}
                      aria-label="Excluir"
                      className="flex h-11 w-11 items-center justify-center rounded-lg text-danger transition-colors hover:bg-danger/10"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      <Modal
        titulo={emEdicao ? "Editar ambiente" : "Novo ambiente"}
        aberto={modalAberto}
        onClose={() => setModalAberto(false)}
        carregando={criar.isPending || atualizar.isPending}
      >
        <FormularioAmbiente
          valoresIniciais={emEdicao}
          carregando={criar.isPending || atualizar.isPending}
          erro={erroForm}
          onSubmit={handleSubmit}
        />
      </Modal>

      <ModalConfirmacao
        aberto={!!paraExcluir}
        titulo="Excluir ambiente"
        mensagem={`Tem certeza que deseja excluir "${paraExcluir?.nome}"?`}
        textoBotaoConfirmar="Excluir ambiente"
        carregando={excluir.isPending}
        onConfirmar={confirmarExclusao}
        onCancelar={() => setParaExcluir(null)}
      />

      <ModalConfirmacao
        aberto={!!paraDesativar}
        titulo="Desativar ambiente"
        mensagem={`Tem certeza que deseja desativar "${paraDesativar?.nome}"? Ele deixará de aparecer como opção em novas reservas e eventos.`}
        textoBotaoConfirmar="Desativar"
        variante="perigo"
        carregando={atualizar.isPending}
        onConfirmar={confirmarDesativacao}
        onCancelar={() => setParaDesativar(null)}
      />
    </>
  );
}
